import { eventServices, eventDifferentials } from "@/mocks/events";
import ScrollReveal from "@/components/base/ScrollReveal";
import { motion } from "framer-motion";

export default function EventsSection() {
  return (
    <section id="eventos" className="relative w-full py-20 md:py-28 overflow-hidden">
      {/* Background image — coffee break / corporate event */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/e2268d4a-73e3-4367-9d65-df24b53a0559/9a8dea02-ace9-46d1-93ba-24f6a3edc5fc_compressed_F-325.webp"
          alt="Coffee break e eventos corporativos Big Mania"
          className="w-full h-full object-cover blur-[2px] scale-110"
        />
      </div>

      {/* Main overlay — graphite radial gradient: lighter center, darker edges */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, rgba(55,55,55,0.65) 0%, rgba(40,40,40,0.78) 50%, rgba(22,22,22,0.88) 100%)",
        }}
      ></div>

      {/* Side vignette — extra depth on left and right edges */}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(to right, rgba(22,22,22,0.45) 0%, transparent 25%, transparent 75%, rgba(22,22,22,0.45) 100%)",
        }}
      ></div>

      {/* Subtle top and bottom fade */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(22,22,22,0.45) 0%, transparent 35%, transparent 65%, rgba(22,22,22,0.55) 100%)",
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <ScrollReveal>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.22em] text-secondary-400 mb-5">
              Eventos Big Mania
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-background-50 leading-tight mb-6">
              Seu evento merece{" "}
              <span className="text-secondary-400">sabor, qualidade</span>
              {" "}e uma experiência inesquecível
            </h2>
            <p className="text-background-100/80 leading-relaxed text-base md:text-lg mb-8 max-w-2xl mx-auto">
              A Big Mania oferece soluções completas para empresas e eventos, com cardápios
              personalizados, alimentos frescos, excelente apresentação e entrega pontual.
            </p>
            {/* Decorative separator */}
            <div className="w-20 h-px mx-auto bg-gradient-to-r from-transparent via-secondary-400/50 to-transparent mb-8"></div>
          </ScrollReveal>

          {/* CTA */}
          <ScrollReveal direction="up" delay={0.1}>
            <motion.a
              href="https://wa.me/558534942701?text=Olá! Gostaria de solicitar um orçamento para evento."
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#F47A3A", color: "#ffffff" }}
            >
              <i className="ri-calendar-event-line text-lg"></i>
              Solicitar orçamento para meu evento
              <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
            </motion.a>
          </ScrollReveal>
        </div>

        {/* Event Service Cards */}
        <ScrollReveal direction="up" delay={0.15} staggerChildren staggerDelay={0.06}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-16">
            {eventServices.map((service, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -3 }}
                transition={{ duration: 0.3 }}
                className="group p-5 rounded-xl bg-foreground-900/45 backdrop-blur-sm border border-foreground-700/15 text-center transition-all duration-400 cursor-default hover:bg-foreground-900/60 hover:border-secondary-500/25"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: "rgba(244, 122, 58, 0.15)" }}
                >
                  <i className={`${service.icon} text-lg`} style={{ color: "#F47A3A" }}></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-background-50 mb-1.5">
                  {service.title}
                </h3>
                <p className="text-xs text-background-100/65 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>

        {/* Differentials */}
        <ScrollReveal direction="up" delay={0.2}>
          <div className="rounded-2xl bg-foreground-900/55 backdrop-blur-sm border border-foreground-700/15 p-8 md:p-10">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 text-center mb-8">
              Por que escolher a Big Mania para seu evento?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {eventDifferentials.map((diff, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-foreground-800/40 border border-foreground-700/10 transition-all duration-300 hover:bg-foreground-800/60 cursor-default"
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "rgba(244, 122, 58, 0.2)" }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: "#F47A3A" }}></i>
                  </div>
                  <span className="text-sm text-background-100/85">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}